/**
 * The events row piece (the home's Upcoming events): every upcoming event in a
 * row, four in view, with arrows and a See all events link once there are more
 * than four. Draws nothing when the studio lists no upcoming event.
 */
import type { SiteEventSummaryDto } from '@telify/shared';

import { definePiece } from '../../contract/define';

/** The parts of one event card, shared by the events row and the events grid. */
export const eventCardParts = {
  item: { description: 'One entry of the list, holding one event card.', required: true },
  card: { description: 'One event; the whole card opens its page.', required: true },
  photo: { description: "The frame of the event's main photo.", required: false, role: 'photo' },
  'photo-image': { description: "The event's main photo.", required: false },
  body: { description: 'The card text under the photo.', required: false },
  date: { description: 'The line under the photo: the day, the month, the weekday and time, and Book.', required: false },
  day: { description: "The day of the month, in the visitor's time zone.", required: false },
  when: { description: 'The month above the weekday and time.', required: false },
  month: { description: "The month, in the visitor's time zone.", required: false },
  time: { description: "The weekday and start time, in the visitor's time zone.", required: false, role: 'time' },
  book: {
    description: "The Book button; reads as unavailable when the event cannot be booked. With the mine option, the mark's button takes its place on an event the client has booked.",
    required: true,
    states: ['unavailable'],
  },
  name: { description: "The event's name.", required: true, role: 'title' },
  summary: { description: "The event's short line.", required: false },
  meta: { description: 'The line of format, spots left and price.', required: false },
  format: { description: 'In person or online.', required: false },
  spots: {
    description: 'How many spots are left, or Full. With the mine option, the mark takes its place on an event the client has booked.',
    required: false,
    states: ['low', 'full'],
  },
  separator: { description: 'The dot between two facts on the meta line.', required: false },
  price: { description: 'The price, or Free.', required: true },
  mine: {
    description: "With the mine option, the signed-in client's own booking of the event: its line (You're going). Its state is where the booking stands.",
    required: false,
    states: ['going', 'held', 'approved', 'requested', 'waitlisted'],
  },
  'mine-action': {
    description:
      "The mark's button in Book's place: the booking's own step while it waits on the client (Complete booking, Register, Pay), else View booking. Without a state when the booking's state could not be read.",
    required: true,
    states: ['going', 'held', 'approved', 'requested', 'waitlisted'],
  },
} as const;

/** web-studio-sites 6.6: the mine option of the events row and the events grid. */
export const eventsMineOption = {
  type: 'boolean',
  default: false,
  description:
    "For a signed-in client, mark each event they have booked with where the booking stands, its button opening the booking in Book's place. A guest sees no mark.",
  showsPart: 'mine',
} as const;

/** web-studio-sites 6.6: the action of the mark's button on an event. */
export const openEventBooking = {
  description: "With the mine option, opens the client's own booking of an event: the step it waits on, else its page. An event with no mark does nothing.",
  args: [{ name: 'eventId', kind: 'item', list: 'events', key: 'id' }],
} as const;

/**
 * web-studio-sites 6.6: where a signed-in client's own booking of a listed event or service stands. going: confirmed;
 * held: the studio booked it for the client (waiting on them, or its forms under the studio's review); approved: a
 * request the studio approved that still waits on payment; requested: a request waiting on the studio; waitlisted.
 */
export type MineState = 'going' | 'held' | 'approved' | 'requested' | 'waitlisted';

/** web-studio-sites 6.6: with the mine option, a signed-in client's own booking of a listed event or service, ready to draw. */
export interface MineMark {
  /** The booking's id. */
  readonly bookingId: string;
  /** Null when the booking's state could not be read (an event's booking waiting on payment, its copy unread): draw only the button. */
  readonly state: MineState | null;
  /** The mark's words: an event's whole line ("You're going"), or on a service the words before its time ("Your next"); null with no state. */
  readonly label: string | null;
  /** The booking's start, a UTC instant (ISO 8601). */
  readonly startsAt: string;
  /** That start in the visitor's time zone: "Sat, Oct 31 at 10:00 AM". */
  readonly when: string;
  /** The booking's page. */
  readonly href: string;
  /** The mark's button: the booking's own step while it waits on the client (Complete booking, Register, Pay), else View booking to its page. */
  readonly action: { readonly label: string; readonly href: string };
}

export const contract = definePiece({
  name: 'events-row',
  description:
    'Every upcoming event in a row by date, four in view at desktop width, with arrows and a See all events link when there are more than four.',
  placeable: true,
  options: {
    showPhoto: { type: 'boolean', default: true, description: "Show each event's main photo.", showsPart: 'photo' },
    showSummary: { type: 'boolean', default: true, description: "Show each event's short line.", showsPart: 'summary' },
    heading: { type: 'string', default: 'Upcoming events', maxLength: 80, description: 'The section heading; null for none.' },
    eyebrow: { type: 'string', default: 'On the calendar', maxLength: 60, description: 'The small label above the heading; null for none.' },
    mine: eventsMineOption,
  },
  parts: {
    section: { description: 'The whole section.', required: false },
    wrap: { description: 'The centered column inside the section.', required: false },
    head: { description: 'The header line: the titles and See all events.', required: false },
    titles: { description: 'The small label above the heading, and the heading.', required: false },
    eyebrow: { description: 'The small label above the heading.', required: false },
    heading: { description: 'The section heading.', required: false, role: 'title' },
    'see-all': { description: 'The See all events link to the events page; only with more than four events.', required: false },
    row: { description: 'The row holding the track and the arrows.', required: false },
    track: { description: 'The scrolling track of event cards.', required: true },
    previous: { description: 'The arrow that brings the earlier four back; hidden at the start.', required: false },
    next: { description: 'The arrow that brings the next four in; hidden at the end.', required: false },
    ...eventCardParts,
  },
  actions: {
    next: { description: 'Brings the next four events into view.', args: [] },
    previous: { description: 'Brings the previous four events back into view.', args: [] },
    scrolledTo: { description: 'Tells the piece which view a swipe or trackpad scroll settled on.', args: [{ name: 'page', kind: 'value', type: 'number', min: 0 }] },
    open: { description: "Opens an event's page.", args: [{ name: 'eventId', kind: 'item', list: 'events', key: 'id' }] },
    seeAll: { description: 'Opens the events page.', args: [] },
    openBooking: openEventBooking,
  },
  events: {
    opened: 'A visitor opened an event.',
    paged: 'The row moved to another view of four.',
  },
});

/** What the events row knows. */
export interface EventsRowState {
  /** loading until the first read lands; error when it failed. */
  readonly status: 'loading' | 'ready' | 'error';
  /** Every upcoming event loaded, by start time; each carries its `spotsLeft`. */
  readonly events: readonly SiteEventSummaryDto[];
  /** How many events one view holds at desktop width (4). */
  readonly perView: number;
  /** The view in place, from 0; the arrows move it by one. */
  readonly page: number;
  /** How many views of four the events fill. */
  readonly pageCount: number;
  /** The index in `events` of the first card in view; the drawing scrolls the track to it. */
  readonly firstInView: number;
  /** The events in view, at most four. */
  readonly inView: readonly SiteEventSummaryDto[];
  /** More than four events: the arrows and See all events show. */
  readonly hasMore: boolean;
  /** An earlier view exists (the previous arrow shows). */
  readonly canPrevious: boolean;
  /** A later view exists (the next arrow shows). */
  readonly canNext: boolean;
  /** The visitor's IANA time zone; dates and times are drawn in it. */
  readonly timezone: string;
  /** The events page's address. */
  readonly allEventsPath: string;
  /** With the mine option, the signed-in client's own booking of each event they have booked, by the event's id; empty for a guest. */
  readonly marks: Readonly<Record<string, MineMark>>;
}

/** What a drawing can ask the events row to do. */
export interface EventsRowActions {
  /** Bring the next four events into view. */
  readonly next: () => void;
  /** Bring the previous four events back into view. */
  readonly previous: () => void;
  /** A swipe or trackpad scroll settled on this view (from 0); out-of-range values are clamped. */
  readonly scrolledTo: (page: number) => void;
  /** Open the page of the event with this id. */
  readonly open: (eventId: string) => void;
  /** Open the events page. */
  readonly seeAll: () => void;
  /** Open the client's own booking of the event with this id: the step it waits on, else its page. An event with no mark does nothing. */
  readonly openBooking: (eventId: string) => void;
}

/** What the events row tells its host. */
export interface EventsRowEvents {
  /** A visitor opened an event; carries its id. */
  readonly opened: { readonly eventId: string };
  /** The row moved to another view; carries the view from 0. */
  readonly paged: { readonly page: number };
}
